import React from "react";
import Tablefilter from "../../../../common/Tablefilter";
import FilterBar from "../../../../library/DataTable/FilterBar";
import VersionModulesExport from "./VersionModulesExport";
import { StyledResetButton } from "../../../../library/BulkToolbarButtons";
import { tokens } from "../../../../../public/tokens";

/*
 * Restyles Tablefilter's own (legacy, generic Bootstrap) trigger button to match this
 * screen's Figma toolbar buttons - same visual spec as FilterBar's own StyledFilterButton -
 * without touching the shared Tablefilter component other tables still rely on as-is.
 */
const FILTER_BUTTON_STYLE = {
    height: "35px",
    padding: `0 ${tokens.globals["spacing-2-5"]}`,
    display: "inline-flex",
    alignItems: "center",
    gap: tokens.globals["spacing-1-5"],
    background: tokens.colors.surfaces["container-background"],
    border: `${tokens.globals["spacing-px"]} solid ${tokens.colors.surfaces["border-divider"]}`,
    borderRadius: tokens.globals["border-radius"],
    boxShadow: tokens.effects.effects.shadow.default,
    color: tokens.colors.text.primary,
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontSize: tokens.typography.fontSize["lg-14px"],
    fontWeight: tokens.typography.fontWeights.semibold,
};

/**
 * Toolbar above the Version Modules table: search, Status/Term/Year filters,
 * Reset, and exports - all left-grouped in that order per Figma. Reset and
 * exports render through `FilterBar`'s `children` slot (same left-hand row)
 * rather than its `onReset`/`rightContent`, which would push them apart via
 * the bar's own `space-between`.
 * @param {object} hook State and handlers from `useVersionModules`.
 */
function VersionModulesToolbar({ hook }) {
    const filters = [
        {
            key: "status",
            component: (
                <Tablefilter
                    filterName="Status"
                    optionArr={hook.statusOptions}
                    state={hook.statusFilter}
                    setState={hook.setStatusFilter}
                    uniqueId="moduleStatus"
                    isDisabled={hook.isProcessing}
                    btnStyleOverride={FILTER_BUTTON_STYLE}
                />
            ),
        },
        {
            key: "term",
            component: (
                <Tablefilter
                    filterName="Term"
                    optionArr={hook.termOptions}
                    state={hook.termFilter}
                    setState={hook.setTermFilter}
                    uniqueId="moduleTerm"
                    isDisabled={hook.isProcessing}
                    btnStyleOverride={FILTER_BUTTON_STYLE}
                />
            ),
        },
        {
            key: "year",
            component: (
                <Tablefilter
                    filterName="Year"
                    optionArr={hook.yearOptions}
                    state={hook.yearFilter}
                    setState={hook.setYearFilter}
                    uniqueId="moduleYear"
                    isDisabled={hook.isProcessing}
                    btnStyleOverride={FILTER_BUTTON_STYLE}
                />
            ),
        },
    ];

    return (
        <FilterBar
            searchValue={hook.search}
            searchPlaceholder="Search"
            searchAriaLabel="Search modules"
            onSearchChange={(value) => {
                hook.setSearch(value);
                hook.setPage(1);
            }}
            filters={filters}
            disabled={hook.isProcessing}
            disabledReason="A bulk action is currently running"
        >
            <StyledResetButton
                type="button"
                onClick={hook.resetFilters}
                disabled={hook.isProcessing}
            >
                <i className="fal fa-undo" aria-hidden="true" />
                Reset
            </StyledResetButton>

            <VersionModulesExport hook={hook} />
        </FilterBar>
    );
}

export default VersionModulesToolbar;
